import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContentfulService } from '../services/contentful.service';
import { ContentfulConfigService } from '../services/contentful-config.service';
import { SupportRequest, SupportRequestCategory } from '../models/classes/support-request.class';
import { ApiService } from '../core/http/api.service';
import { ActivityTrackingService } from '../services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { GcmLoadingService, GcmMatDialogService, GcmNotificationService, Notification, NotificationType } from '@gcm/gcm-ui-angular';
import { ContactPageResponse } from '../models/interfaces/contentful-response.interface';

@Component({
    selector: 'app-contact',
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './contact.component.html',
    styleUrls: ['./contact.component.scss']
})
export class ContactComponent {
  private formBuilder = inject(FormBuilder);
  private contentfulConfig = inject(ContentfulConfigService);
  private contentfulService = inject(ContentfulService);
  private apiService = inject(ApiService);
  private loadingService = inject(GcmLoadingService);
  private notificationService = inject(GcmNotificationService);
  private activityTracking = inject(ActivityTrackingService);
  private dialogService = inject(GcmMatDialogService);

  private readonly contactPageData = toSignal(
    this.contentfulService.fetchCommonAssets('contactPage', this.contentfulConfig.contactPage).pipe(
      map(data => data as ContactPageResponse)
    ),
    { initialValue: null as unknown as ContactPageResponse }
  );
  readonly labelTexts = computed(() => {
    const data = this.contactPageData();
    return data
      ? this.contentfulService.buildKeyValueLabels(data.data.landingPage.sectionsCollection.items[0].resourcesCollection.items)
      : {} as Record<string, string>;
  });
  readonly optionValues = computed(() =>
    this.contactPageData()?.data.landingPage.sectionsCollection.items[1].resourcesCollection.items ?? [] as Record<string, string>[]
  );
  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(
      filter(data => !!data)
    ),
    { initialValue: {} as Record<string, string> }
  );

  contactForm: FormGroup = this.formBuilder.group(
    {
      subject: ['', [Validators.required, Validators.maxLength(256)]],
      category: ['', Validators.required],
      message: ['', [Validators.required, Validators.maxLength(1000)]],
    }
  );

  constructor() {
    this.activityTracking.landedOnPage('Contact');
  }

  get form() {
    return this.contactForm.controls;
  }

  submitContactForm() {
    if (this.contactForm.valid) {
      this.activityTracking.submittedRequest();
      const request = new SupportRequest(this.contactForm.value);
      request.category = this.form.category.value.trim() === "Data Inquiries" ? SupportRequestCategory.DataInquiry : (this.form.category.value.trim() === "Technical Support" ? SupportRequestCategory.TechnicalSupport : SupportRequestCategory.Unknown);
      const loadingTask = 'submitContactForm';
      this.loadingService.addAsyncTask(loadingTask);
      this.apiService.contactSupport(request).subscribe({
        next: () => {
          this.contactForm.reset({ category: '' });
          this.loadingService.resolveAsyncTask(loadingTask);
          this.notificationService.notify(new Notification("Your request was successfully submitted. The GCM team will respond soon", "Contact", NotificationType.success));
        },
        error: () => {
          this.loadingService.resolveAsyncTask(loadingTask);
          this.notificationService.notify(new Notification(`Your request was not submitted. Please try again or contact the GCM team directly at <a href="mailto:grosvenor.reporting@gcmlp.com">grosvenor.reporting@gcmlp.com</a>`, "Contact", NotificationType.error));
        }
      });
    }
    else {
      this.contactForm.markAllAsTouched();
    }
  }

  canDeactivate() {
    if (this.contactForm.value.subject || this.contactForm.value.category || this.contactForm.value.message) {
      return this.dialogService.showConfirmationDialog(this.labelTexts()['contact.wb.msg'] as string, this.labelTexts()['contact.wb.submsg'] as string, this.labelTexts()['contact.wb.leave.pagebtn'] as string, this.labelTexts()['contact.wb.continuebtn'] as string);
    }
    return true;
  }
} 
